/**
 * D245: number notation. Suffixes by default (`10.2Kc`, D244's money rule), with a
 * scientific toggle in settings (`1.02e4c`). Per device, in localStorage; storage that
 * throws (a private window) leaves the default.
 */
import { useEffect, useState } from 'preact/hooks';
import { fmtBig, SHORTEN_FROM } from '../juice/format.ts';
import { currencyShort } from './terms.ts';

export const SCI_KEY = 'hedgerow-sci-numbers';

function read(): boolean {
  try {
    return globalThis.localStorage?.getItem(SCI_KEY) === '1';
  } catch {
    return false;
  }
}

let sci = read();
const listeners = new Set<(on: boolean) => void>();

export function isScientific(): boolean {
  return sci;
}

export function setScientific(on: boolean): void {
  sci = on;
  try {
    globalThis.localStorage?.setItem(SCI_KEY, on ? '1' : '0');
  } catch {
    // Kept for this session only.
  }
  for (const fn of listeners) fn(on);
}

/** Read the stored choice again. For tests. */
export function reloadScientific(): boolean {
  sci = read();
  return sci;
}

/** The settings row: `[on, set]`. A screen that calls it draws again when it changes. */
export function useScientific(): [boolean, (on: boolean) => void] {
  const [value, setValue] = useState(sci);
  useEffect(() => {
    listeners.add(setValue);
    return () => void listeners.delete(setValue);
  }, []);
  return [value, setScientific];
}

/** `12345` -> `1.23e4`. Cut down, never rounded up, like `fmtBig`. */
export function fmtSci(n: number): string {
  if (!Number.isFinite(n)) return '0';
  if (n < 0) return `-${fmtSci(-n)}`;
  if (n < SHORTEN_FROM) return fmtBig(n);
  let exp = Math.floor(Math.log10(n));
  let m = n / 10 ** exp;
  if (m >= 10) {
    m /= 10;
    exp += 1;
  }
  const cut = Math.floor(m * 100 + 1e-9) / 100;
  return `${cut.toFixed(2).replace(/\.?0+$/, '')}e${exp}`;
}

/** A count in the notation chosen on this device. */
export function num(n: number, scientific = sci): string {
  return scientific ? fmtSci(n) : fmtBig(n);
}

/** Money: `300c`, `10.2Kc`, or `1.02e4c`. */
export function coins(n: number, scientific = sci): string {
  return `${num(n, scientific)}${currencyShort()}`;
}

/** A gain as it pops: `+5c`. */
export function gain(n: number, scientific = sci): string {
  return `+${coins(n, scientific)}`;
}
